import { ButtonBase, Stack, Typography } from '@mui/material'
import React, { FC, useMemo } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import {
  Maybe,
  TicketStatusDetailFragment,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'

type TicketSelectProps = {
  selected: boolean
  throughTicketIndex?: number
  serial: UserItemSerialDetailFragment
  onSelect: (serialId: string) => void
}

export const TicketSelect: FC<TicketSelectProps> = ({
  serial,
  throughTicketIndex,
  selected,
  onSelect,
}) => {
  const canUseTicket = useMemo(
    () =>
      checkUserCanUseTicket(serial.serial?.ticketStatus, throughTicketIndex),
    [serial.serial?.ticketStatus, throughTicketIndex]
  )

  const label = useMemo(() => {
    if (
      throughTicketIndex !== undefined &&
      serial.serial?.ticketStatus?.throughTicket?.[throughTicketIndex].seat
    ) {
      return serial.serial?.ticketStatus?.throughTicket?.[throughTicketIndex]
        .seat
    }
    return (
      serial.serial?.formattedAssignedNumber ?? serial.formattedSerialNumber
    )
  }, [
    serial.formattedSerialNumber,
    serial.serial?.formattedAssignedNumber,
    serial.serial?.ticketStatus?.throughTicket,
    throughTicketIndex,
  ])

  const colorThemeApp = useMemo(() => {
    if (config.applicationKey === 'greenticket') {
      return {
        icon: 'secondary.dark',
        bg: 'secondary.light',
      }
    }
    return {
      icon: 'primary.main',
      bg: 'grey.50',
    }
  }, [config.applicationKey])

  return (
    <ButtonBase
      disabled={!canUseTicket}
      sx={{
        width: '100%',
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: selected ? colorThemeApp.bg : 'grey.200',
        bgcolor: selected ? colorThemeApp.bg : 'transparent',
        p: 4,
        '&.Mui-disabled': {
          bgcolor: 'grey.100',
          borderColor: 'grey.100',
        },
      }}
      onClick={() => onSelect(serial.serialId)}
    >
      <Stack flex={1} direction="row" spacing={2} alignItems="center">
        {selected || !canUseTicket ? (
          <Icons.Complete
            sx={{
              color: !canUseTicket ? 'grey.50' : colorThemeApp.icon,
            }}
          />
        ) : (
          <IconButton.Ticket
            bgcolor="grey.50"
            color="grey.700"
            sx={{
              pointerEvents: 'none',
              width: 24,
              height: 24,
              svg: { fontSize: '16px' },
            }}
          />
        )}
        <Stack flex={1}>
          <Typography
            variant="h3"
            align="left"
            color={!canUseTicket ? 'text.disabled' : 'text.primary'}
          >
            {label}
          </Typography>
        </Stack>
      </Stack>
    </ButtonBase>
  )
}

/**
 * check user can use ticket
 * @param ticketStatus
 * @param throughTicketIndex
 */
export function checkUserCanUseTicket(
  ticketStatus: Maybe<TicketStatusDetailFragment> | undefined,
  throughTicketIndex: number | undefined
): boolean {
  // check ticket expired
  if (ticketStatus?.isExpired) {
    return false
  }
  // check throughTicket is used
  if (throughTicketIndex !== undefined) {
    return !ticketStatus?.throughTicket?.[throughTicketIndex].isUsed
  }
  return !!ticketStatus?.canUseTicket
}
